import { client } from './claims';

export interface BackfillProcessorStatus {
  enabled: boolean;
  batchSize: number;
  intervalMinutes: number;
  statusCounts: Record<string, number>;
  lastRun: {
    time: string | null;
    results: any;
  };
  nextRun: string | null;
  jobActive: boolean;
}

export interface BackfillListItem {
  id: string;
  processorStatus: string;
  processorAction: string | null;
  aiCategory: string | null;
  aiConfidence: number | null;
  aiLenderName: string | null;
  aiClientName: string | null;
  aiClaimReference: string | null;
  emailFrom: string | null;
  emailSubject: string | null;
  emailDate: string | null;
  createdAt: string | null;
  promotedTaskId: string | null;
  processorRanAt: string | null;
  processorAttempts: number | null;
}

export interface BackfillListResponse {
  items: BackfillListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BackfillStats {
  total: number;
  statusCounts: Record<string, number>;
  actionCounts: Record<string, number>;
  categoryCounts: Record<string, number>;
}

export interface BackfillAnalysis {
  buckets: Record<string, number>;
  topSenders: { from: string; count: number }[];
  topLenders: { lender: string; count: number }[];
  monthly: { month: string; count: number }[];
}

export interface BackfillListQuery {
  page?: number;
  pageSize?: number;
  processorStatus?: string;
  processorAction?: string;
  aiCategory?: string;
  q?: string;
  sort?: string;
  dir?: 'asc' | 'desc';
}

const base = '/ui/admin';

export const getBackfillProcessorStatus = () =>
  client.get<BackfillProcessorStatus>(`${base}/processors/backfill/status`).then(r => r.data);

export const enableBackfillProcessor = () =>
  client.post<{ success: boolean; message: string }>(`${base}/processors/backfill/enable`).then(r => r.data);

export const disableBackfillProcessor = () =>
  client.post<{ success: boolean; message: string }>(`${base}/processors/backfill/disable`).then(r => r.data);

export const runBackfillProcessor = (batchSize = 25) =>
  client.post<{ success: boolean; results: any }>(`${base}/processors/backfill/run`, { batchSize }, { timeout: 120000 }).then(r => r.data);

export const listBackfillRows = (q: BackfillListQuery = {}) => {
  const params: any = {};
  if (q.page) params.page = q.page;
  if (q.pageSize) params.pageSize = q.pageSize;
  if (q.processorStatus) params.processorStatus = q.processorStatus;
  if (q.processorAction) params.processorAction = q.processorAction;
  if (q.aiCategory) params.aiCategory = q.aiCategory;
  if (q.q) params.q = q.q;
  if (q.sort) params.sort = q.sort;
  if (q.dir) params.dir = q.dir;
  return client.get<BackfillListResponse>(`${base}/backfill/list`, { params }).then(r => r.data);
};

export const getBackfillStats = () =>
  client.get<BackfillStats>(`${base}/backfill/stats`).then(r => r.data);

export const getBackfillAnalysis = () =>
  client.get<BackfillAnalysis>(`${base}/backfill/analysis`).then(r => r.data);

export const getBackfillRow = (id: string) =>
  client.get<any>(`${base}/backfill/${id}`).then(r => r.data);

export const promoteBackfillRow = (id: string) =>
  client.post<{ success: boolean; promotedTaskId: string }>(`${base}/backfill/${id}/promote`).then(r => r.data);

export const archiveBackfillRow = (id: string, reason?: string) =>
  client.post<{ success: boolean }>(`${base}/backfill/${id}/archive`, { reason }).then(r => r.data);
